import React from "react";
import {
  JobApplication,
  JobApplicationStatus,
  ProfileMatchStatus,
  QuestionnaireStatus,
} from "../../../Entities/JobApplication";
import { PaginationType } from "../../../Interface";
import { Button, Empty, Table, Tag, Tooltip, Typography } from "antd";
import {
  CalendarOutlined,
  EyeOutlined,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

interface CandidateJobApplicationsTableProps {
  jobApplications: JobApplication[];
  loading: boolean;
  pagination: PaginationType;
  handlePageChange: (page: number) => void;
}

const getProfileScreeningTag = (
  profileMatchStatus: ProfileMatchStatus | null,
) => {
  if (!profileMatchStatus) {
    return <Tag color="blue">Pending</Tag>;
  }
  if (profileMatchStatus.status === "pending") {
    return <Tag color="blue">Pending</Tag>;
  } else if (profileMatchStatus.status === "qualified") {
    return <Tag color="green">Qualified</Tag>;
  } else {
    return <Tag color="error">Unqualified</Tag>;
  }
};

const getAssessmentTag = (status: QuestionnaireStatus | null) => {
  if (!status) {
    return <Typography.Text>-</Typography.Text>;
  }

  if (status === "pending") {
    return <Tag color="blue">In progress</Tag>;
  } else if (status === "qualified") {
    return <Tag color="success">Qualified</Tag>;
  } else if (status === "unqualified") {
    return <Tag color="error">Unqualified</Tag>;
  } else if (status === "action_required") {
    return <Typography.Text type="warning">Action Required</Typography.Text>;
  } else {
    return <Tag color="warning">Partially Qualified</Tag>;
  }
};

const getStatusTag = (status: JobApplicationStatus) => {
  if (status === "applied") {
    return <Tag color="default">Applied</Tag>;
  } else if (status === "shortlisted") {
    return <Tag color="yellow">Shortlisted</Tag>;
  } else if (status === "interview") {
    return <Tag color="magenta">Interview</Tag>;
  } else if (status === "rejected") {
    return <Tag color="error">Rejected</Tag>;
  } else if (status === "hired") {
    return <Tag color="success">Hired</Tag>;
  } else {
    return <Tag color="default">{status}</Tag>;
  }
};

const getOnlineInterviewTag = (application: JobApplication) => {
  const { interviewSchedule, status } = application;
  if (interviewSchedule) {
    const isPast = dayjs(interviewSchedule).isBefore(dayjs());
    if (isPast) {
      return <Tag color="success">Completed</Tag>;
    } else {
      return <Tag color="processing">Upcoming</Tag>;
    }
  } else {
    if (status !== "interview") {
      return <Typography.Text>-</Typography.Text>;
    } else if (status === "interview") {
      return (
        <Typography.Text className="text-wrap">
          Slot Selection Pending
        </Typography.Text>
      );
    } else {
      return <Typography.Text>-</Typography.Text>;
    }
  }
};

const CandidateJobApplicationsTable: React.FC<
  CandidateJobApplicationsTableProps
> = ({ jobApplications, loading, pagination, handlePageChange }) => {
  const navigate = useNavigate();

  const handleViewApplicationDetails = (application: JobApplication) => {
    const url = `/application-details?applicationId=${application._id}&jobId=${application.jobPost._id}`;
    navigate(url);
  };

  const columns: ColumnsType<JobApplication> = [
    {
      title: "Applied On",
      dataIndex: "applied_date",
      key: "applied_date",
      align: "start",
      width: 150,
      render: (_, record) => (
        <Typography.Text>
          <CalendarOutlined />{" "}
          {dayjs(record.applied_date).format("DD MMM YYYY")}
        </Typography.Text>
      ),
    },
    {
      title: "Job Opening",
      dataIndex: "jobPost",
      key: "jobPost",
      align: "start",
      width: 200,
      render: (_, record) => (
        <Typography.Text className="text-wrap">
          {record.jobPost.title}
        </Typography.Text>
      ),
    },
    {
      title: "Profile Screening",
      key: "profileMatchStatus",
      dataIndex: "profileMatchStatus",
      align: "center",
      width: 150,
      render: (_: unknown, record) =>
        getProfileScreeningTag(record?.profileMatchStatus || null),
    },
    {
      title: "Assessment",
      key: "questionnaireStatus",
      dataIndex: "questionnaireStatus",
      align: "center",
      width: 150,
      render: (_: unknown, record) =>
        getAssessmentTag(record?.questionnaireStatus || null),
    },
    {
      title: "Online Interview",
      key: "interviewSchedule",
      dataIndex: "interviewSchedule",
      align: "center",
      width: 150,
      render: (_: unknown, record) => getOnlineInterviewTag(record),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      align: "center",
      width: 130,
      render: (_: unknown, record) => getStatusTag(record.status),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_: unknown, record) => (
        <Tooltip placement="bottom" title="View Application">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewApplicationDetails(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table<JobApplication>
      rowKey="_id"
      bordered={false}
      columns={columns}
      dataSource={jobApplications}
      loading={{
        spinning: loading,
        indicator: <Loading3QuartersOutlined spin={loading} />,
        tip: "Please wait...",
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No job applications found."
          />
        ),
      }}
      pagination={{
        current: pagination.page,
        pageSize: pagination.limit,
        total: pagination.totalRecords,
        showSizeChanger: false,
        onChange: handlePageChange,
        showTotal: (total, range) =>
          `Showing ${range[0]}–${range[1]} of ${total} applications${total !== 1 ? "s" : ""}`,
      }}
      scroll={{ y: "calc(100vh - 490px)", x: 1030 }}
    />
  );
};

export default CandidateJobApplicationsTable;
